/**
 * Sieve — stats & streak (§8). Streak rules mirror Wordle/Noodle exactly:
 * a streak increments on a win and resets if the previous day was not won.
 */

import type { Outcome, Stats } from './types';

/**
 * Applies a finished DAILY result to the stats block. Archive replays never
 * call this (§8). `consecutive` says whether this puzzle directly follows the
 * previously-completed daily (id === lastCompletedDailyId + 1); a gap means a
 * missed day, which resets the streak before this result is counted.
 */
export function applyDailyResult(
  stats: Stats,
  outcome: Outcome,
  consecutive: boolean,
): Stats {
  const next: Stats = {
    ...stats,
    mistakeHistogram: [...stats.mistakeHistogram] as Stats['mistakeHistogram'],
  };

  next.played += 1;

  const won = outcome === 'flawless' || outcome === 'solved';

  // A missed day breaks the streak regardless of today's result.
  if (!consecutive) next.currentStreak = 0;

  if (won) {
    next.won += 1;
    next.currentStreak += 1;
    if (next.currentStreak > next.maxStreak) next.maxStreak = next.currentStreak;
  } else {
    next.currentStreak = 0;
  }

  if (outcome === 'flawless') next.flawless += 1;

  return next;
}

/** Records the mistake count into the histogram bucket (§8). */
export function recordMistakes(stats: Stats, outcome: Outcome, mistakes: number): Stats {
  const histogram = [...stats.mistakeHistogram] as Stats['mistakeHistogram'];
  if (outcome === 'failed') histogram[3] += 1;
  else histogram[Math.min(mistakes, 2) as 0 | 1 | 2] += 1;
  return { ...stats, mistakeHistogram: histogram };
}

/** Win % = wins / attempted, matching Wordle and Noodle (§8). */
export function winPercent(stats: Stats): number {
  return stats.played === 0 ? 0 : Math.round((stats.won / stats.played) * 100);
}

/** Flawless % over games played. */
export function flawlessPercent(stats: Stats): number {
  return stats.played === 0 ? 0 : Math.round((stats.flawless / stats.played) * 100);
}
